import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  AfterViewInit
} from '@angular/core';
import { V2BottomSheetRef } from '../../../../v2-components/helpers/v2-bottom-sheet-ref';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../../models/item-modifier-ui.model';
import { ModifierGroupService } from 'services/modifier-group.service';
import { filter, map } from 'rxjs/operators';
import { NestedModGroupSheetStore } from 'stores/nested-mod-group-sheet.store';
import { RxUtil } from 'utility/rx.util';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';

@Component({
  selector: 'nested-modifier-group-sheet',
  templateUrl: 'nested-modifier-group.sheet.html',
  styleUrls: ['nested-modifier-group.sheet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NestedModifierGroupSheet implements OnInit, AfterViewInit {
  private readonly _parentModifier$ =
    new BehaviorSubject<ItemModifierUI | null>(null);

  readonly parentModifier$: Observable<ItemModifierUI> =
    this._parentModifier$.pipe(filter(RxUtil.inputIsNotNullOrUndefined));
  readonly isTopLevelSheet$ = new BehaviorSubject(false);
  readonly showValidationState$ = new BehaviorSubject(false);

  private currentModifierGroupSheetParentModifierSub?: Subscription;
  private currentModifierGroupSheetParentModifierId?: string;

  constructor(
    public bottomSheetRef: V2BottomSheetRef,
    public modifierGroupService: ModifierGroupService,
    private nestedModGroupSheetStore: NestedModGroupSheetStore,
    private hostElement: ElementRef<HTMLElement>
  ) {}

  async ngOnInit(): Promise<void> {
    // take from this while the modifier id matches
    this.currentModifierGroupSheetParentModifierSub =
      this.nestedModGroupSheetStore.currentModifierGroupSheetParentModifier$.subscribe(
        (currentModifierGroupSheetParentModifier) => {
          if (!this.currentModifierGroupSheetParentModifierId) {
            this.currentModifierGroupSheetParentModifierId =
              currentModifierGroupSheetParentModifier.id;
            this._parentModifier$.next(currentModifierGroupSheetParentModifier);
          } else if (
            currentModifierGroupSheetParentModifier.id ===
            this.currentModifierGroupSheetParentModifierId
          ) {
            this._parentModifier$.next(currentModifierGroupSheetParentModifier);
          }
        }
      );

    this.isTopLevelSheet$.next(
      await RxUtil.takeOne(
        this.nestedModGroupSheetStore.isTopLevelOfModifierGroupSheetStack$
      )
    );
  }

  ngAfterViewInit() {
    if (this.nestedModGroupSheetStore.currentScrollBottomSheetToElementById) {
      this.scrollElementIntoView(
        this.nestedModGroupSheetStore.currentScrollBottomSheetToElementById
      );
      this.nestedModGroupSheetStore.clearCurrentScrollBottomSheetToElementById();
    }
  }

  async goBack(): Promise<void> {
    this.nestedModGroupSheetStore.popSheetStack();
    await this.modifierGroupService.openNestedModifierGroupSheet(
      await RxUtil.takeOne(
        this.nestedModGroupSheetStore.currentModifierGroupSheetParentModifier$
      ),
      true
    );
  }

  private isEveryModGroupNotRequired(
    modGroups: ItemModifierGroupUI[]
  ): boolean {
    return modGroups.every((mg) => mg.minimum === 0);
  }

  private hasUserMadeSelections(modGroups: ItemModifierGroupUI[]): boolean {
    return modGroups.some((mg) => mg.modifiers.some((m) => m.isSelected));
  }

  readonly shouldShowSkipButton$: Observable<boolean> =
    this.parentModifier$.pipe(
      map(
        (parentModifier) =>
          this.isEveryModGroupNotRequired(parentModifier.modifierGroups) &&
          !this.hasUserMadeSelections(parentModifier.modifierGroups)
      )
    );

  readonly shouldShowNextButton$: Observable<boolean> =
    this.shouldShowSkipButton$.pipe(
      map((shouldShowSkipButton) => !shouldShowSkipButton)
    );

  async proceed(): Promise<void> {
    this.showValidationState$.next(true);

    // ensure all selections are valid before proceeding
    const parentModifier = await RxUtil.takeOne(this.parentModifier$);
    const canProceed = parentModifier.modifierGroups.every(
      (mg) => mg.isSelectionValid
    );

    if (!canProceed) {
      // scroll to first invalid mod group
      const firstInvalidModGroup = this.hostElement.nativeElement.querySelector(
        '.modifier-card.invalid'
      );
      if (firstInvalidModGroup) {
        return scrollIntoView(firstInvalidModGroup, {
          behavior: 'smooth',
          block: 'start'
        });
      }

      return;
    }

    // check all modifiers in mod groups for this sheet to see if they are
    // selected and have child mod groups that are invalid, if so open that sheet
    for (const mg of parentModifier.modifierGroups) {
      for (const m of mg.modifiers) {
        if (m.isSelected) {
          for (const nestedMg of m.modifierGroups) {
            if (!nestedMg.isSelectionValid) {
              return this.modifierGroupService.openNestedModifierGroupSheet(m);
            }
          }
        }
      }
    }

    if (this.isTopLevelSheet$.getValue()) {
      this.bottomSheetRef.close();
      return;
    }

    await this.goBack();
  }

  scrollElementIntoView(id: string) {
    const element = this.hostElement.nativeElement.querySelector(
      '[id = "' + id + '"]'
    );
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
}
